import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@tmap/mmm-style-guide/src/Button';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { Paper } from '@tmap/mmm-style-guide/src/Paper';
import { Divider } from '@tmap/mmm-style-guide/src/Divider';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { LockIcon, LocalActivity } from '@tmap/mmm-style-guide/src/Icon';

import copy from 'clipboard-copy';

import FullWrapper from '../layouts/fullWrapper';
import Metatags from '../components/metatags';
import useRequireAuth from '../hooks/useRequireAuth';
import useQuestionModule from '../hooks/useQuestionModule';
import ResponsivePicture from '../components/responsivePicture';
import client from '../client';
import setCacheHeaders from '../lib/setCacheHeaders';
import ColorBlocks from '../layouts/colorBlocks';

const PictureOutline = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: '400px',
  overflow: 'hidden',
}));

const PerksBubbles = styled('div')(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  height: '200px',
  width: '100%',
  [theme.breakpoints.down('md')]: {
    height: '80px',
  },
}));

const PerksBubbleBox = styled('div', {
  shouldForwardProp: (p) => !['size', 'left', 'top'].includes(p),
})(({
  theme,
  size,
  left,
  top,
}) => ({
  width: `${size}px`,
  height: `${size}px`,
  position: 'absolute',
  left: `${left}%`,
  top: `${top}%`,
  borderRadius: `${size}px`,
  border: '4px solid #FFFFFF33',
  overflow: 'hidden',
  boxShadow: '0px 3px 14px 2px #0000001F, 0px 8px 10px 1px #00000024, box-shadow: 0px 5px 5px -3px #00000033',
  [theme.breakpoints.down('md')]: {
    transform: 'scale(0.5) translate(-50%, -50%)',
  },
}));

function PerksBubble(props) {
  const {
    logo,
    size,
    left,
    top,
  } = props;
  return (
    <PerksBubbleBox size={size} left={left} top={top}>
      <ResponsivePicture image={logo} width={size} height={size} style={{ background: '#fff' }} />
    </PerksBubbleBox>
  );
}

const HeroWrapper = styled('div')(({ theme }) => ({
  background: `radial-gradient(50% 50% at 50% 50%, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.veryDark} 100%)`,
  paddingBottom: theme.spacing(10),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const HeroTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.blue.light,
  textAlign: 'center',
  fontSize: '60px!important',
  fontWeight: 900,
  lineHeight: '120%',
  letterSpacing: '-0.5px',
  textShadow: '0px 3px 14px #0000001F, 0px 8px 10px #00000024, 0px 5px 5px #00000033',
  marginLeft: theme.spacing(3),
  marginRight: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    fontSize: '50px!important',
  },
  [theme.breakpoints.down('sm')]: {
    fontWeight: 700,
    letterSpacing: '0.25px',
    fontSize: '28px!important',
  },
}));

const HeroDescTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  maxWidth: '715px',
  textAlign: 'center',
  fontSize: '16px',
  letterSpacing: '0.15px',
  marginTop: theme.spacing(1),
  textShadow: '0px 1px 8px #0000001F, 0px 3px 4px #00000024, 0px 3px 3px #00000033',
  marginLeft: theme.spacing(3),
  marginRight: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    maxWidth: '500px',
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '450px',
  },
}));

const HeroButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(5),
  '& .MuiButtonBase-root': {
    borderRadius: '8px',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    '& .MuiButtonBase-root': {
      width: '100%',
    },
  },
}));

const HeadingTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '28px!important',
  letterSpacing: '0.15px',
  lineHeight: '124%',
  marginBottom: theme.spacing(6),
  [theme.breakpoints.down('md')]: {
    fontSize: '24px!important',
  },
}));

const SignupOverlay = styled('div')(({ theme }) => ({
  width: '100%',
  '&>div:first-of-type': {
    filter: 'blur(3px)',
    maxHeight: '300px',
    overflow: 'hidden',
    marginBottom: '-150px',
    [theme.breakpoints.down('md')]: {
      maxHeight: '200px',
      marginBottom: '-100px',
    },
  },
  '&>div:last-of-type': {
    position: 'relative',
    zIndex: 1,
    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.1) 6.5%, rgba(255, 255, 255, 0.3) 15%, #FFFFFF 55%)',
    textAlign: 'center',
    [theme.breakpoints.down('md')]: {
      background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, #FFFFFF 25%)',
      marginLeft: theme.spacing(-3),
      marginRight: theme.spacing(-3),

    },
  },
}));

const PerkCardWrapper = styled(Paper)(({ theme }) => ({
  '&>div:first-of-type': {
    padding: theme.spacing(4, 2.75),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(4, 0),
    },
  },
  '&>hr': {
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(-4),
      marginRight: theme.spacing(-4),
    },
  },
}));

const RedeemButton = styled(Button)(({ theme }) => ({
  '& .MuiButtonBase-root': {
    backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='${theme.palette.secondary.main.replace('#', '%23')}' stroke-width='4' stroke-dasharray='10, 2' stroke-dashoffset='5'/%3e%3c/svg%3e");`,
    backgroundRepeat: 'no-repeat',
    borderRadius: '8px',
    transition: `background 0.25s ${theme.transitions.easing.easeInOut}`,
    '&:hover, &:focus-visible': {
      border: '0',
      backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='${theme.palette.secondary.dark.replace('#', '%23')}' stroke-width='4' stroke-dasharray='10, 2' stroke-dashoffset='5'/%3e%3c/svg%3e");`,
    },
    border: '0',
    width: '239px',
    maxWidth: '100%',
    height: '42px',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
}));

const CouponWrapper = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(1),
  background: theme.palette.grey[100],
  borderRadius: '8px',
  overflow: 'hidden',
  display: 'inline-block',
  '& .MuiTypography-root': {
    padding: theme.spacing(0.5, 2),
  },
}));

const CopyButton = styled(Button)(() => ({
  '& .MuiButtonBase-root': {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    boxShadow: 'none',
  },
}));

function PerkCard(props) {
  const {
    perk,
    disableRedeem,
    disableReason,
    onRedeemDisabled,
  } = props;
  const [copied, setCopied] = useState(false);
  const handleCopy = (e, source = 'copy') => {
    copy(perk.couponCode).then(() => {
      setCopied(source);
    });
  };
  useEffect(() => {
    let timeout;
    if (copied) {
      timeout = setTimeout(() => {
        setCopied(false);
      }, 2500);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [copied]);

  const handleRedeem = () => {
    if (disableRedeem) {
      if (onRedeemDisabled) onRedeemDisabled();
    } else if (perk.redeemUrl) {
      window.open(perk.redeemUrl);
    } else {
      handleCopy(null, 'redeem');
    }
  };
  const redeemText = copied === 'redeem' ? 'Copied Code!' : (perk.redeemText || 'Redeem Now');
  return (
    <PerkCardWrapper elevation={0}>
      <div>
        <Grid container spacing={2} alignItems={disableRedeem ? 'center' : 'flex-start'}>
          <Grid item>
            <PictureOutline>
              <ResponsivePicture image={perk.logo} width={64} height={64} />
            </PictureOutline>
          </Grid>
          <Grid item xs>
            <Typography variant='body2' color='text.disabled'>{perk.companyName}</Typography>
            <Typography variant='h6' fontWeight={700}>{perk.perkTitle}</Typography>
            {!disableRedeem && <Typography variant='body2' color='textSecondary'>{perk.perkDescription}</Typography>}
            {!disableRedeem && perk.couponCode && (
              <CouponWrapper>
                <Grid container alignItems='center' flexWrap='nowrap'>
                  <Grid item xs>
                    <Typography variant='subtitle2' color={copied ? 'secondary' : 'textPrimary'}>
                      {perk.couponCode}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <CopyButton variant='contained' color='inverted' size='small' onClick={handleCopy}>
                      {copied === 'copy' ? 'Copied!' : 'Copy'}
                    </CopyButton>
                  </Grid>
                </Grid>
              </CouponWrapper>
            )}
          </Grid>
          <Grid item xs={12} md='auto'>
            <RedeemButton
              variant='outlined'
              color='secondary'
              startIcon={disableRedeem ? <LockIcon /> : <LocalActivity />}
              onClick={handleRedeem}
            >
              {disableRedeem ? disableReason : redeemText}
            </RedeemButton>
          </Grid>
        </Grid>
      </div>
      <Divider />
    </PerkCardWrapper>
  );
}

function Perks(props) {
  const { perks } = props;
  const { isLoading, isAuthenticated } = useRequireAuth();
  const eligibilityModule = useQuestionModule('eligibility');
  const handleLoginRedirect = useCallback(() => {
    eligibilityModule?.requireCompletion('/perks');
  }, [eligibilityModule]);
  const disableRedeem = isLoading || !isAuthenticated || !eligibilityModule?.isComplete;
  const disableReason = isAuthenticated ? 'Complete Profile' : 'Sign in to Redeem';
  const limitedPerks = [...perks].slice(0, 5);
  const blurPerks = [limitedPerks.pop(), limitedPerks.pop()];
  const perksBubbles = new Array(9).fill(null).map((x, i) => perks[i % perks.length]);
  return (
    <>
      <Metatags
        title='MakeMyMove Exclusive Perks'
        description='MakeMyMove Members gain access to exclusive discounts and perks'
        canonical={`${process.env.NEXT_PUBLIC_CANONICAL_DOMAIN}/404`}
      />
      <FullWrapper>
        <HeroWrapper>
          <PerksBubbles>
            {perksBubbles.map((perk, i) => (
              <PerksBubble
                logo={perk.logo}
                size={[77, 88, 66, 50, 50, 70, 77, 88, 65][i]}
                left={[16, 73.6, 41.9, 28.9, 61.5, -1.2, 95, 6.5, 86.5][i]}
                top={[41.5, 32.5, 28, -5, -8, 54.5, 54.5, -11.5, -8][i]}
                key={perk._id}
              />
            ))}
          </PerksBubbles>
          <HeroTypography variant='h1'>
            {isAuthenticated ? (
              <>Perks to Power<br />Your Next Chapter</>
            ) : (
              <>Unlock Perks to<br />Power Your Move</>
            )}
          </HeroTypography>
          <HeroDescTypography variant='body1'>
            {(isAuthenticated && eligibilityModule?.isComplete) ? (
              <>
                We know moving is hard. We’ve gathered discounts and helpful partnerships to lighten
                the load, stretch your budget, and help you feel at home faster.
              </>
            ) : (
              <>
                We know moving is hard. Complete your profile to access discounts and helpful
                partnerships to lighten the load, stretch your budget, and help you feel
                at home faster.
              </>
            )}
          </HeroDescTypography>
          {!isAuthenticated && (
            <HeroButton size='large' variant='contained' color='convert' loading={isLoading} onClick={handleLoginRedirect}>
              Sign Up to Redeem Perks
            </HeroButton>
          )}
          {isAuthenticated && !eligibilityModule?.isComplete && (
            <HeroButton size='large' variant='contained' color='convert' loading={isLoading || !eligibilityModule} onClick={handleLoginRedirect}>
              Complete Profile and Redeem Perks
            </HeroButton>
          )}
        </HeroWrapper>
        <ColorBlocks.Block overflow='sm'>
          <HeadingTypography variant='h4'>Browse the Perks</HeadingTypography>
          {isAuthenticated ? (
            perks.map((perk) => (
              <PerkCard
                perk={perk}
                key={perk._id}
                disableRedeem={disableRedeem}
                disableReason={disableReason}
                onRedeemDisabled={handleLoginRedirect}
              />
            ))
          ) : (
            <>
              {limitedPerks.map((perk) => (
                <PerkCard
                  perk={perk}
                  key={perk._id}
                  disableRedeem={disableRedeem}
                  disableReason={disableReason}
                  onRedeemDisabled={handleLoginRedirect}
                />
              ))}
              <SignupOverlay>
                <div>
                  {blurPerks.map((perk) => (
                    <PerkCard
                      perk={perk}
                      key={perk._id}
                      disableRedeem={disableRedeem}
                      disableReason={disableReason}
                      onRedeemDisabled={handleLoginRedirect}
                    />
                  ))}
                </div>
                <Box px={1}>
                  <Typography variant='h4' fontWeight={700} mb={1} fontSize='28px!important'>
                    Sign up to see the rest
                  </Typography>
                  <Typography variant='body1' marginBottom={3}>
                    MakeMyMove members gain access to exclusive perks
                    such as coupon codes and discounts.
                    <br />
                    Answer a few questions when you sign up to view and redeem perks.
                  </Typography>
                  <HeroButton size='large' variant='contained' color='convert' loading={isLoading} onClick={handleLoginRedirect}>
                    Sign Up to Redeem Perks
                  </HeroButton>
                </Box>
              </SignupOverlay>
            </>
          )}
        </ColorBlocks.Block>
      </FullWrapper>
    </>
  );
}

export const getServerSideProps = async (context) => {
  const perks = await client.fetch(`
      *[_type == "perk"] | order(_updatedAt desc)
    `);

  setCacheHeaders(context.res);

  const analyticsProps = {
    pageName: 'perks',
  };

  return {
    props:
    {
      analyticsProps,
      perks,
    },
  };
};

export default Perks;
